import * as R from 'ramda';
import {
    Category,
    Feature,
    Functional,
    LatLngBounds,
    Level,
    Ordinal,
    Position,
    Venue,
    Structure,
    Entity,
} from '@wayfinder/vms-base';

import { LOADING_STATUS_LOADED, LOADING_STATUS_LOADING } from './constants';
import * as LokiDb from './loki-db';

// ---------------------------------------------------------------------------------------------------------------------
// get all the objects of a given type
// ---------------------------------------------------------------------------------------------------------------------

/*
 * Get all the Features
 * @sig getFeatures -> [Feature]
 */
const getFeatures = () => LokiDb.getFeatures();

// ---------------------------------------------------------------------------------------------------------------------
// look up an object by id
// ---------------------------------------------------------------------------------------------------------------------

// simple lookup that throws if the id isn't there
const throwIfNil = (prop, id, o) => {
    if (R.isNil(o)) throw new Error(`There is no ${prop} with id ${id}`);

    return o;
};

/*
 * Get a level (or throw an exception)
 * @sig getLevel :: (LevelId, Bool) -> Level
 */
const getLevel = (levelId, state, throwIfMissing = true) =>
    throwIfMissing
        ? throwIfNil('level', levelId, R.path(['landscapeState', 'levels', levelId], state))
        : R.path(['landscapeState', 'levels', levelId], state);

/*
 * Get a structure (or throw an exception)
 * @sig getStructure :: (StructureId, Bool) -> Structure
 */
const getStructure = (structureId, state, throwIfMissing = true) =>
    throwIfMissing
        ? throwIfNil('structure', structureId, R.path(['landscapeState', 'structures', structureId], state))
        : R.path(['landscapeState', 'structures', structureId], state);

/*
 * Get a feature (or throw an exception)
 * @sig getFeature :: FeatureId -> Feature
 */
const getFeature = (featureId, throwIfMissing = true) =>
    throwIfMissing ? throwIfNil('feature', featureId, LokiDb.getFeature(featureId)) : LokiDb.getFeature(featureId);

const getVenue = (venueId, state) => R.pathOr({}, ['landscapeState', 'venue', venueId], state);
const getOrdinals = (venueId, state) => R.values(R.path(['landscapeState', 'ordinals'], state));
const getStructures = (venueId, state) => R.values(R.path(['landscapeState', 'structures'], state));
const getLevels = (venueId, state) => R.values(R.path(['landscapeState', 'levels'], state));

/*
 * Get an ordinal (or throw an exception)
 * @sig getOrdinal :: OrdinalId -> Ordinal
 */
const getOrdinal = (ordinalId, state, throwIfMissing = true) =>
    throwIfMissing
        ? throwIfNil('ordinal', ordinalId, R.path(['landscapeState', 'ordinals', ordinalId], state))
        : R.path(['landscapeState', 'ordinals', ordinalId], state);

/*
 * Get an ordinal (or throw an exception)
 * @sig getOrdinalFromOrdinalNumber :: OrdinalNumber -> Ordinal
 */
const getOrdinalFromOrdinalNumber = (n, state) => {
    const ordinals = R.values(R.path(['landscapeState', 'ordinals'], state));
    const ordinalNumber = Number.parseInt(n, 10);
    return R.find(o => o.ordinalNumber === ordinalNumber, ordinals);
};

/*
 * @sig toOrdinal :: Ordinal|OrdinalId|Number -> Ordinal|undefined
 */
const getOrdinalFromOrdinalOrIdOrNumber = (ordinalOrIdOrNumber, state) => {
    // Is x an integer or a string containing an integer?
    const isIntegerString = x => Number.isInteger(Number.parseInt(x, 10));

    if (R.is(Object, ordinalOrIdOrNumber)) return ordinalOrIdOrNumber;
    if (isIntegerString(ordinalOrIdOrNumber)) return getOrdinalFromOrdinalNumber(ordinalOrIdOrNumber, state);
    return getOrdinal(ordinalOrIdOrNumber, state, false);
};

/*
 * Get a Venue (or throw an exception)
 * @sig getVenueSummary :: VenueId -> Venue
 *
 * TODO: default sense of throwIfMissing is the OPPOSITE of all the other functions because
 * more getVenueSummary calls expect no exception on error
 */
const getVenueSummary = (venueId, throwIfMissing = false) =>
    throwIfMissing
        ? throwIfNil('venueSummary', venueId, LokiDb.getVenueSummary(venueId))
        : LokiDb.getVenueSummary(venueId);

/*
 * Get a 'poi'
 * @sig getPoi :: (VenueId, poiId)
 */
const getPoi = (venueId, poiId) =>
    poiId === `${venueId}_NEW` ? { uuid: `${venueId}_NEW`, venueId } : LokiDb.findEntity({ uuid: poiId });

// Look up an Entity using its VMS "poiId" which is currently constructed by <venue-id>_<entity-id>
const findEntityByPoiId = poiId => LokiDb.findEntity({ uuid: poiId });

// ---------------------------------------------------------------------------------------------------------------------
// get the Entities for an object
// ---------------------------------------------------------------------------------------------------------------------

const entitiesForVenue = venueId => R.filter(e => e.venueId === venueId, LokiDb.getEntities());

// ---------------------------------------------------------------------------------------------------------------------
// get the Features for an object
// ---------------------------------------------------------------------------------------------------------------------

// :: (String, String) -> Feature -> Boolean
const featureMatchesPropertyValue = (prop, expected) => feature => R.path(['properties', prop], feature) === expected;

// :: (String, String) -> [Feature]
const _featuresWithPropertyValue = (prop, id) => R.filter(featureMatchesPropertyValue(prop, id), getFeatures());

// :: [Feature] -> [FeatureId]
const _pluckFeatureIds = R.map(R.path(['properties', 'subid']));

/*
 * Return the features for the Level from its featureIds
 * @sig featuresForLevel :: LevelId -> [Feature]
 */
const featuresForLevel = levelId => _featuresWithPropertyValue('levelId', levelId);

/*
 * Return the features for the Ordinal from its featureIds
 * @sig featuresForOrdinal :: OrdinalId -> [Feature]
 */
const featuresForOrdinal = ordinalId => _featuresWithPropertyValue('ordinalId', ordinalId);

/*
 * Return all the features with the given EntityId (as opposed to FeatureId)
 * @sig featuresForEntity :: EntityId -> [Feature]
 */
const featuresForEntity = entityId => _featuresWithPropertyValue('id', entityId);

const pointFeaturesForEntity = entityId => {
    const features = featuresForEntity(entityId);
    const points = R.filter(Functional.pathIs(['geometry', 'type'], 'Point'), features);
    return points;
};

/*
 * Return all the feature ids of the polygons with the  given EntityId
 * @sig polygonIdsForEntity :: EntityId -> [FeatureId]
 */
const polygonIdsForEntity = entityId => {
    const id = entityId ? entityId.replace(/#/g, '') : '';
    const features = featuresForEntity(id);
    const polygons = R.filter(Functional.pathIs(['geometry', 'type'], 'Polygon'), features);
    return _pluckFeatureIds(polygons);
};

/*
 * Return true if entity is a Point, false otherwise
 * @sig isEntityAPoint :: EntityId -> bool
 */
const isEntityAMovablePoint = entityId => {
    const features = featuresForEntity(entityId);
    const category = R.path(['properties', 'category'], R.head(features));
    const pointPoiCategoriesDoNotMove = [
        'checkin',
        'element.door',
        'gate',
        'gate.arrival',
        'gate.departures',
        'parking',
        'parking.accessible',
        'parking.ev',
        'portal',
        'portal.bus',
        'portal.elevator',
        'portal.escalator',
        'portal.movingwalkway',
        'portal.ramp',
        'portal.stairs',
        'portal.train',
        'restroom',
        'restroom.female',
        'restroom.male',
        'restroom.family',
        'restroom.unisex',
        'security',
        'security.checkpoint',
        'security.customs',
        'security.exit',
    ];

    // If there is only one point in the feature, then the entity is a Point
    return features.length === 1 && !R.isNil(category) && !R.includes(category, pointPoiCategoriesDoNotMove);
};

/*
 * Return the features for the background
 * @sig featuresForBackground :: () -> [Feature]
 */
const featuresForBackground = () => _featuresWithPropertyValue('ordinalId', Ordinal.LANDSCAPE_BACKGROUND);

// featureIds
const featureIdsForEntity = entityId => _pluckFeatureIds(featuresForEntity(entityId));

// ---------------------------------------------------------------------------------------------------------------------
// get the objects related to a feature
// ---------------------------------------------------------------------------------------------------------------------

const _featureProperty = (prop, feature) => R.path(['properties', prop], feature);

/*
 * Return the *first* (of very likely only one) levelIds for the feature
 * @sig :: levelIdForFeature Feature|FeatureId -> [LevelId]
 */
const levelIdForFeature = featureOrFeatureId => {
    const feature = R.is(String, featureOrFeatureId) ? getFeature(featureOrFeatureId) : featureOrFeatureId;
    return _featureProperty('levelId', feature);
};

// ---------------------------------------------------------------------------------------------------------------------
// get the LevelIds for an object
// ---------------------------------------------------------------------------------------------------------------------

const venueIdFromStructureId = structureId => (structureId ? structureId.split('-')[0] : '');

const levelsForStructureId = (structureId, state) =>
    R.filter(l => l.structureId === structureId, getLevels(venueIdFromStructureId(structureId), state));
const levelsForStructure = (structure, state) =>
    R.filter(l => l.structureId === structure.id, getLevels(venueIdFromStructureId(structure.id), state));

/*
 * Return all the ordinalIds for the named venue
 * @sig ordinalsForVenue :: VenueId -> [Ordinal]
 */
const ordinalsForVenue = (venueId, state) => {
    const ordinals = R.values(R.path(['landscapeState', 'ordinals'], state));
    return R.filter(o => o.venueId === venueId, ordinals);
};

const defaultOrdinalForVenue = (venueId, state) => {
    const ordinals = ordinalsForVenue(venueId, state);
    return Ordinal.getDefaultOrdinalForVenue(ordinals);
};

const defaultStructureForVenue = (venueId, state) => {
    const structures = getStructuresForVenue(venueId, state);
    return Structure.getDefaultStructureForVenue(structures);
};

/*
 * Return all the ordinalIds for the named venue
 * @sig ordinalIdsForVenue :: (VenueId, State) -> [Ordinal]
 */
const ordinalIdsForVenue = (venueId, state) => R.pluck('id', ordinalsForVenue(venueId, state));

/*
 * Return the ordinal for the Level specified by LevelId
 * @sig ordinalForLevel :: (LevelId, State) -> Ordinal
 */
const ordinalForLevel = (levelId, state) => {
    const level = R.path(['landscapeState', 'levels', levelId], state);
    return R.path(['landscapeState', 'ordinals', level.ordinalId], state);
};

const ordinalIdForLevel = (levelId, state) => R.prop('id', ordinalForLevel(levelId, state));

/*
 * Return all the levels for the named venue
 * @sig levelsForVenue :: (VenueId, State) -> [Level]
 */
const levelsForVenue = (venueId, state) => R.filter(l => l.venueId === venueId, getLevels(venueId, state));

/*
 * Return all the levelIds for the named venue
 * @sig levelIdsForVenue :: VenueId -> [LevelId]
 */
const levelIdsForVenue = (venueId, state) => R.pluck('id', levelsForVenue(venueId, state));

/*
 * Get the theme
 * @sig getTheme :: (String, State) -> Theme
 */
const getTheme = (_, state) => state.landscapeState.theme;

/*
 * Get the lastChangeAt
 * @sig getLastChangedAt :: State -> Number
 */
const getLastChangedAt = state => state.landscapeState.lastChangedAt;

/*
 * Get the loadingStatus for the given path: undefined|LOADING_STATUS_LOADING|LOADING_STATUS_LOADED
 * @sig getLoadingStatus :: ([String], State) -> undefined|String
 */
const getLoadingStatus = (path, state) => R.path(R.prepend('loadingStatus', path), state);

/*
 * Returns the dropdown options for the display tab category icons.
 * getBadgeOptions :: (Category, Boolean) -> [{ label, value, badge }]
 */
const getBadgeOptions = (category, isV5, venueId, accountId, state) => {
    const venueCategory = getVenueCategory(venueId, state);
    let badgeOptions;
    if (isV5) {
        badgeOptions = [Category.createBadgeDropdown(category, venueCategory, venueId, accountId)];
    } else {
        const v4CanonicalNames = Category.getV4Names(category);
        badgeOptions = Category.createV4BadgeDropdown(v4CanonicalNames);
    }
    return badgeOptions;
};

/*
 * Get the categories for a non internal user along with the portals
 * Also reject the unoccupied category for an external user. External users should
 * make a polygon unoccupied through the trash can button.
 * We need the portals so the category is visible in the display tab but a non internal user
 * will not be able to edit that entity
 * @sig nonInternalUserCategories :: [Category] -> [Category]
 */
const nonInternalUserCategories = categories => {
    const portals = R.filter(c => c.canonicalName.includes('portal'), categories);
    const nonInternal = R.reject(c => c.internalOnly, categories);

    return R.concat(nonInternal, portals);
};

// @sig categoriesForVenue :: VenueId -> [Category]
const categoriesForVenue = (venueId, internal, state) => {
    const theme = getTheme(venueId, state);
    const categories = Category.fromTheme(venueId, theme);

    return internal ? categories : nonInternalUserCategories(categories);
};

/*
 * Get the category for the given canonicalName
 * @sig getCategory :: String -> Category
 */
const getCategory = (venueId, canonicalName, state) => {
    const theme = getTheme(venueId, state);
    const category = theme ? theme[canonicalName] : null;

    return category ? Category.toCategory(venueId)(category, canonicalName) : {};
};

/*
 * Get the category for the given venue
 * @sig getVenueCategory :: VenueId -> Category
 */
const getVenueCategory = (venueId, state) => getVenue(venueId, state).venueCategory || 'airport';

const noFeatures = (entityId, features, state) => {
    const entity = findEntity({ id: entityId });
    const venueId = entity;
    const featuresLoaded = featureLoadingStatus(state)[venueId] === 'LOADED';

    return featuresLoaded && features.length === 0 && isVenueV5(venueId, state);
};

/*
 * Return a Position for the Entity by looking up its features
 * @sig positionForEntity :: (EntityId, State) -> Position
 *
 */
const positionForEntity = (entityId, state) => {
    const features = featuresForEntity(entityId);

    if (noFeatures(entityId, features, state)) throw new Error(`No features for entity ${entityId}`);

    const f0 = features[0];
    const levelId = levelIdForFeature(f0);
    const latLng = Feature.center(f0);
    return Position.fromLatLngAndLevel(latLng, levelId);
};

// :: LevelId -> VenueId
const venueIdForLevel = (levelId, state) => R.prop('venueId', getLevel(levelId, state));

// :: LevelId -> StructureId
const structureIdForLevel = (levelId, state) => R.prop('structureId', getLevel(levelId, state));

const venueSummaries = state => R.path(['landscapeState', 'venueSummaries'], state);

// Sorts structures by (descending) name and includes only those that 'shouldDisplay'
const getStructuresForVenue = (venueId, state) =>
    R.filter(s => s.venueId === venueId, R.values(state.landscapeState.structures));

// :: VenueId -> [Structure]
const _organizeStructures = (venueId, state) => {
    // :: [{ name: ... }] -> [{ name: ... }]
    const reverseSortByName = R.pipe(R.sortBy(R.prop('name')), R.reverse);

    const displayableStructures = R.filter(s => s.shouldDisplay, getStructuresForVenue(venueId, state));
    return R.map(
        Functional.assocUsingFunc('levels', structure => levelsForStructure(structure, state)),
        reverseSortByName(displayableStructures),
    );
};

// :: Compute a cache key for memoization from the venueId and last update time for STRUCTURES
const _structuresToString = (venueId, state) => R.keys(state.landscapeState.structures);

// Sorts structures by (descending) name and includes only those that 'shouldDisplay'
// :: VenueId -> [Structure]
const organizeStructures = R.memoizeWith(_structuresToString, _organizeStructures);

// :: LevelId -> StructureId
const getStructureIdFromLevelId = (levelId, state) => R.prop('structureId', getLevel(levelId, state, false));

// :: LevelId -> Structure
const getStructureFromLevelId = (levelId, state) =>
    getStructure(getStructureIdFromLevelId(levelId, state), state, false);

const getLevelOrDefault = (venueId, structureId, levelId, state) => {
    const level = getLevel(levelId, state, false);

    if (level) return level;

    if (!structureId) structureId = R.propOr('', 'id', defaultStructureForVenue(venueId, state));

    const levels = levelsForStructureId(structureId, state);
    return R.find(l => l.isDefaultForStructure, levels);
};

const getStructureOrDefault = (venueId, structureId, levelId, state) => {
    // get the structure from the structureId or levelid
    const structure = structureId ? getStructure(structureId, state, false) : getStructureFromLevelId(levelId, state);

    if (structure) return structure;

    // if no structure, default to the default structure of the venue
    return defaultStructureForVenue(venueId, state);
};

// @sig defaultLevelFor :: StructureId -> LevelId
const defaultLevelIdForStructure = (structureId, state) => {
    const levels = levelsForStructureId(structureId, state);
    const defaultLevel = R.find(R.propEq(true, 'isDefaultForStructure'), levels);
    return defaultLevel.id;
};

// Return a Level

/*
 * Try to find the Level on the specified Ordinal at lngLat
 * @sig levelAtLatLng :: (LatLng, OrdinalId) -> Level|undefined
 */
const levelAtLatLng = (venueId, latLng, ordinalId, state, pickOneAnyway = false) => {
    if (!latLng.lat && !latLng.lng) return;

    // :: LevelId -> Boolean
    const isLatLngInBounds = levelId => {
        const boundingBox = getBoundingBoxForLevel(levelId, state);
        return boundingBox ? LatLngBounds.isPointInBounds(latLng, boundingBox) : false;
    };

    let result = R.find(isLatLngInBounds, Level.getLevelIdsForOrdinal(ordinalId, getLevels(venueId, state)));

    // TODO: should we "pickOneAnyway" if you clicked outside any bounds?
    if (!result && pickOneAnyway)
        result = R.path([0, 'id'], Level.getLevelsForOrdinal(ordinalId, getLevels(venueId, state)));

    return result;
};

const getVenueCenter = (venueId, state) => {
    const venue = getVenue(venueId, state);

    if (venue) return Venue.getVenueCenter(venue);
};

const featureLoadingStatus = state => R.path(['landscapeState', 'loadingStatus', 'features'], state);
const venueSummariesLoadingStatus = state => R.path(['landscapeState', 'loadingStatus', 'venueSummaries'], state);
const isLoadingVenueList = state => LOADING_STATUS_LOADING === venueSummariesLoadingStatus(state);
const hasVenueListLoaded = state => LOADING_STATUS_LOADED === venueSummariesLoadingStatus(state);

const mapLoadingStatus = state => R.path(['landscapeState', 'loadingStatus', 'map'], state);
const venueLoadingStatus = state => R.path(['landscapeState', 'loadingStatus', 'overallVenue'], state);

const isLoadingVenue = state => LOADING_STATUS_LOADING === venueLoadingStatus(state);
const hasVenueLoaded = state => LOADING_STATUS_LOADED === venueLoadingStatus(state);
const getVenueLoadingError = state => R.path(['landscapeState', 'loadingStatus', 'overallVenueError'], state);

const notYetLoadingMap = state => undefined === mapLoadingStatus(state);
const isLoadingMap = state => LOADING_STATUS_LOADING === mapLoadingStatus(state);
const hasMapLoaded = state => LOADING_STATUS_LOADED === mapLoadingStatus(state);
const getMapLoadingError = state => R.path(['landscapeState', 'loadingStatus', 'mapError'], state);

// LOKI!
const findLevelsForTerminal = LokiDb.findLevelsForTerminal;
const findTerminalsForVenue = LokiDb.findTerminalsForVenue;
const getVenueSummaries = LokiDb.getVenueSummaries;
const searchVenueSummaries = LokiDb.searchVenueSummaries;
const searchEntities = LokiDb.searchEntities;
const findEntity = LokiDb.findEntity;
const getNextId = LokiDb.getNextId;
const getNextEntityId = LokiDb.getNextEntityId;
const buildTagsArray = LokiDb.buildTagTuples;

/*
 * Return an object where each key is the name of a terminal and each value is an array of LevelIds for that terminal
 * @sig terminalsToLevels :: VenueId -> { String: [LevelId] }
 */
const terminalsToLevels = venueId => {
    const terminals = findTerminalsForVenue(venueId);
    const levelIds = terminalName => findLevelsForTerminal(venueId, terminalName);
    return R.zipObj(terminals, R.map(levelIds, terminals));
};

/*
 * Return the levelId of an Entity using the levelId of the Entity's first feature
 * @sig levelIdForEntity :: Entity -> LevelId|undefined
 */
const levelIdForEntity = Functional.pipe(
    R.prop('id'), // -> EntityID
    featuresForEntity, // -> [Feature]
    R.head, // -> Feature
    levelIdForFeature, // -> LevelId
);

/*
 * Get the formatVersion for a venue
 * @sig getVenueAssetFormatVersion :: VenueId -> String
 */
const getVenueAssetFormatVersion = (venueId, state) => getVenue(venueId, state).assetFormatVersion || 'v5';

/*
 * Get the formatVersion for a venue
 * @sig isVenueV5 :: VenueId -> String
 */
const isVenueV5 = (venueId, state) => getVenueAssetFormatVersion(venueId, state) === 'v5';

/*
 * Get the bounding box for a venue
 * @sig getBoundingBoxForVenue :: VenueId -> CardinalPoints
 */
const getBoundingBoxForVenue = (venueId, state) => getVenue(venueId, state).boundingBox;

const getBoundingBoxForBasemap = (venueId, state) => R.path(['landscapeState', 'boundingBox'], state);

/*
 * Get the bounding box for the given structure
 * @sig getBoundingBoxForStructure :: (StructureId, State) -> BoundingBox
 */
const getBoundingBoxForStructure = (structureId, masterState) => {
    const structure = getStructure(structureId, masterState);
    return structure.boundingBox;
};

/*
 * Get the bounding box for the given Level
 * @sig getBoundingBoxForLevel :: (LevelId, State) -> BoundingBox
 */
const getBoundingBoxForLevel = (levelId, masterState) => {
    const level = getLevel(levelId, masterState);
    return level.boundingBox;
};

const getMapStyle = state => R.path(['landscapeState', 'mapStyle'], state);

const getSprites = state => R.path(['landscapeState', 'sprites'], state);

const getVenueDropdowns = state => R.path(['landscapeState', 'venueDropdowns'], state); // for spritesheet generator

const getNextFeatureId = venueId => {
    const entityId = getNextEntityId(venueId);
    const featureId = LokiDb.getNextFeatureId(venueId);

    if (!Number.isInteger(featureId)) return entityId;

    return Math.max(entityId, featureId);
};

/*
 * This should just be 'createEntity'...
 * createEntity should take an entity with the id of 'VENUEID-NEW' and give it a uuid 'VENUEID-###'
 */
const createIdForEntity = (venueId, entity) => {
    const numericId = getNextFeatureId(venueId);
    const id = String(numericId);
    // const uuid = `${ venueId }_${ id }`
    const enrichEntity = { id, numericId /* , uuid */ };
    return R.mergeRight(entity, enrichEntity);
};

/*
 * Returns a default entity object to use as a placeholder when adding an entity to the map.
 * Gets the current floor and building
 * @sig createDefaultEntity :: (VenueId, LevelId, LatLng, Boolean, Int) -> Entity
 */
const createDefaultEntity = (venueId, levelId, latLng, ordinalNumber, includeBadge, showName, security, state) => {
    const structure = getStructureFromLevelId(levelId, state);
    const level = getLevel(levelId, state);
    const entity = Entity.createDefaultEntity(
        latLng,
        level,
        structure,
        ordinalNumber,
        includeBadge,
        showName,
        security,
    );

    return createIdForEntity(venueId, entity);
};

const getIsEntityMigrated = (venueId, state) => R.pathOr(false, ['landscapeState', 'isEntityMigrated'], state);

const getIsFeatureMigrated = (venueId, state) => R.pathOr(false, ['landscapeState', 'isFeatureMigrated'], state);

// ---------------------------------------------------------------------------------------------------------------------

const selectors = {
    getNextFeatureId,
    createDefaultEntity,
    createIdForEntity,

    getFeature,
    getFeatures,
    getLevel,
    getOrdinal,
    getOrdinalFromOrdinalNumber,
    getOrdinalFromOrdinalOrIdOrNumber,
    getStructure,
    getVenueCenter,

    entitiesForVenue,

    levelIdsForVenue,

    pointFeaturesForEntity,
    featureIdsForEntity,
    polygonIdsForEntity,
    isEntityAMovablePoint,

    defaultOrdinalForVenue,
    defaultStructureForVenue,
    levelsForVenue,
    ordinalsForVenue,
    ordinalIdsForVenue,

    ordinalIdForLevel,
    ordinalForLevel,

    positionForEntity,

    featuresForBackground,
    featuresForOrdinal,
    featuresForLevel,
    featuresForEntity,

    levelIdForFeature,
    levelIdForEntity,

    getTheme,
    getOrdinals,
    getStructures,
    getStructureOrDefault,
    getLevelOrDefault,
    getLevels,

    getLoadingStatus,
    getBadgeOptions,

    structureIdForLevel,
    venueIdForLevel,

    getPoi,
    categoriesForVenue,
    getCategory,

    venueSummaries,

    // loki
    getVenueSummaries,
    searchVenueSummaries,
    searchEntities,
    findEntity,
    findEntityByPoiId,
    findTerminalsForVenue,
    getNextId,

    findLevelsForTerminal,
    buildTagsArray,
    terminalsToLevels,
    getNextEntityId,

    getVenue,
    getVenueCategory,
    getVenueSummary,
    getVenueAssetFormatVersion,
    isVenueV5,

    // "data groomers"
    organizeStructures,
    getStructureFromLevelId,
    defaultLevelIdForStructure,

    levelAtLatLng,
    getLastChangedAt,

    isLoadingVenueList,
    hasVenueListLoaded,

    notYetLoadingMap,
    isLoadingMap,
    isLoadingVenue,
    hasVenueLoaded,
    hasMapLoaded,
    getVenueLoadingError,
    getMapLoadingError,

    getBoundingBoxForBasemap,
    getBoundingBoxForLevel,
    getBoundingBoxForStructure,
    getBoundingBoxForVenue,

    getMapStyle,
    getSprites,
    getVenueDropdowns,

    getIsEntityMigrated,
    getIsFeatureMigrated,
};

export { selectors };
